import { icModel } from 'generated-ts-common';
import { isEmptyString } from './Util';
import { DefaultDateFormatter, intl } from '../lib';

/**
 * Converts the given number to a string representing its file-size in a human-readable format.
 *
 * @author agd
 * @param value the size value to convert
 * @return the correctly formatted size
 */
export const convertNumberToFileSize = (value: number): string => {
  const factor = 1024;
  const units = ['B', 'kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  let chosenUnit;
  for (let i = 0; i < units.length; i++) {
    if (i !== units.length - 1 && value / factor >= 1) {
      value = value / factor;
    } else {
      chosenUnit = units[i];
      break;
    }
  }

  return Math.round(value * 10) / 10 + ' ' + chosenUnit;
};

/**
 * Formats a patient's address into a readable string format.
 *
 * @param address the address of a patient
 * @author agd
 * @return the formatted address
 */
export function formatPatientAddress(
  address: icModel.saf.infrastructure.model.patient.EnhancedPatient['address']
): string {
  // build address string
  let fullAddress = !isEmptyString(address.streetName)
    ? ''
    : address.streetName;
  if (!isEmptyString(address.postalCode)) {
    if (!isEmptyString(fullAddress)) {
      fullAddress += ', ';
    }
    fullAddress += address.postalCode;
  }

  if (!isEmptyString(address.city)) {
    if (!isEmptyString(fullAddress)) {
      fullAddress += ', ';
    }
    fullAddress += address.city;
  }

  if (!isEmptyString(address.country)) {
    if (!isEmptyString(fullAddress)) {
      fullAddress += ', ';
    }
    fullAddress += address.country;
  }

  return fullAddress;
}

/**
 * Calculates the number of years between now and the given date.
 *
 * @param date the date to calculate the years for
 * @author agd
 * @return the years since the date
 */
export function calculateYearsForDate(date: Date): number {
  const diff_ms = Date.now() - date.getTime();
  const age_dt = new Date(diff_ms);
  return Math.abs(age_dt.getUTCFullYear() - 1970);
}
/**
 * Creates a formatted birthday text from the given dateOfBirth.
 *
 * @param dateOfBirth the birthdate. This can either be a string or a Date object.
 * @return the string: 02/29/1984 (Age: 39)
 * @author tomas lukac
 */
export function formatPersonBirthDate(dateOfBirth: string | Date): string {
  const { age, formattedDate } = personAgeFormatHelper(dateOfBirth);
  return `${formattedDate} (${intl('view.patient.age')}: ${age})`;
}

/**
 * Interface for personAgeFormatHelper function
 */
interface PersonAgeFormatHelper {
  /** person's age */
  age: number | Date;
  /** String containing translated value 'year' or 'years' depending on person's age  */
  translatedString: string;
  /** Formatted the given date  */
  formattedDate: string;
}

/**
 * Helper function returns calculated age, correct string ie: year / years and formatted date.
 * @param dateOfBirth – the birthdate. This can either be a string or a Date object
 * @return  age, translatedString (year or years), formattedDate
 */
export function personAgeFormatHelper(
  dateOfBirth: string | Date
): PersonAgeFormatHelper {
  // calculate the age from the given dateOfBirth and format it according to the mockup
  let age;
  if (typeof dateOfBirth === 'string') {
    age = calculateYearsForDate(new Date(dateOfBirth));
  } else {
    age = dateOfBirth;
  }
  const translatedString =
    age === 1
      ? intl('view.time.interval.year')
      : intl('view.time.interval.years');
  return {
    age,
    translatedString,
    formattedDate: DefaultDateFormatter.formatDate(dateOfBirth)
  };
}

/**
 * Removes seconds from date - floors it on whole minutes.
 * Converts result on date if input is string or number.
 *
 * @param date date - could be inserted in several formats
 * @return date floored on whole minutes
 * @author Jozef Mizerik
 */
export function removeSecondsFromDate(
  date: Date | string | number | null
): Date | undefined {
  if (date === null) {
    return;
  }
  let unixTimeStamp: number = date as number;
  if (typeof date === 'string') {
    unixTimeStamp = new Date(date).getTime();
  } else if (date instanceof Date) {
    unixTimeStamp = date.getTime();
  }
  // 1 minute = 60000 milliseconds
  return new Date(unixTimeStamp - (unixTimeStamp % 60000));
}
